import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Plus, Briefcase } from "lucide-react";
import EmptyState from "@/components/common/EmptyState";
import StatusBadge from "@/components/common/StatusBadge";
import ResourceDrawer from "@/components/common/ResourceDrawer";

export default function Projects() {
    const [items, setItems] = useState([]);
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const [customers, setCustomers] = useState([]);

    const load = async () => {
        const [p, c] = await Promise.all([api.get("/projects"), api.get("/customers")]);
        setItems(p.data); setCustomers(c.data);
    };
    useEffect(() => { load(); }, []);

    const fields = [
        { name: "name", label: "Project name", span: 2 },
        { name: "code", label: "Code" },
        { name: "customer_id", label: "Customer", type: "select", options: customers.map((c) => ({ value: c.id, label: c.name })) },
        { name: "billing_method", label: "Billing", type: "select", options: [{ value: "fixed_price", label: "Fixed price" }, { value: "per_hour", label: "Per hour" }] },
        { name: "budget", label: "Budget", type: "number" },
        { name: "hourly_rate", label: "Hourly rate", type: "number" },
        { name: "start_date", label: "Start date", type: "date" },
        { name: "end_date", label: "End date", type: "date" },
        { name: "description", label: "Description", type: "textarea", span: 2 },
    ];

    const save = async (f) => {
        await api.post("/projects", f);
        await load();
    };

    return (
        <>
            <PageHeader kicker="PROJECTS" title="Projects" subtitle="Track engagements, budgets, and profitability."
                actions={<Button onClick={() => setDrawer({ open: true, item: null })} data-testid="project-new"><Plus className="w-4 h-4 mr-1.5" />New project</Button>} />
            <PageBody>
                {items.length === 0 ? <EmptyState icon={Briefcase} title="No projects yet" actionLabel="Create project" onAction={() => setDrawer({ open: true, item: null })} /> :
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((p) => (
                            <div key={p.id} className="bg-card border border-border rounded-md p-5">
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{p.code || "PRJ"}</div>
                                    <StatusBadge status={p.status} />
                                </div>
                                <div className="text-[17px] font-semibold mt-2">{p.name}</div>
                                <div className="text-[12px] text-muted-foreground mt-1">{p.customer_name || "Internal"}</div>
                                <div className="border-t border-border mt-4 pt-3 grid grid-cols-2 gap-2 text-[12px]">
                                    <div><div className="text-muted-foreground">Budget</div><div className="font-mono">{fmtMoney(p.budget)}</div></div>
                                    <div><div className="text-muted-foreground">Rate</div><div className="font-mono">{fmtMoney(p.hourly_rate)}/hr</div></div>
                                </div>
                            </div>
                        ))}
                    </div>}
            </PageBody>
            <ResourceDrawer open={drawer.open} onClose={() => setDrawer({ open: false, item: null })} title="New project" fields={fields} initial={{ status: "active", billing_method: "fixed_price" }} onSave={save} />
        </>
    );
}
